import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { FileText, Save } from 'lucide-react';

const LegalManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [privacyContent, setPrivacyContent] = useState('');

  useEffect(() => {
    fetchLegalDocuments();
  }, []);

  const fetchLegalDocuments = async () => {
    try {
      const [termsRes, privacyRes] = await Promise.all([
        axios.get('/legal/terms'),
        axios.get('/legal/privacy')
      ]);
      
      setTermsContent(termsRes.data.content || '');
      setPrivacyContent(privacyRes.data.content || '');
    } catch (error) {
      console.error('Failed to fetch legal documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (docType, content) => {
    setSaving(true);
    try {
      await axios.post('/admin/legal', {
        doc_type: docType,
        content: content
      });
      toast.success(`${docType === 'terms' ? 'Terms of Service' : 'Privacy Policy'} updated successfully!`);
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center">Loading...</div>;
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Legal Documents</h1>
        <p className="text-gray-600 mt-1">Manage Terms of Service and Privacy Policy</p>
      </div>

      {/* Terms of Service */}
      <Card>
        <CardHeader className="bg-blue-50">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Terms of Service
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <textarea
            value={termsContent}
            onChange={(e) => setTermsContent(e.target.value)}
            rows={12}
            className="w-full px-3 py-2 border rounded-md font-mono text-sm"
            placeholder="Enter Terms of Service content..."
          />
          <div className="mt-4">
            <Button
              onClick={() => handleSave('terms', termsContent)}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Terms of Service
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Privacy Policy */}
      <Card>
        <CardHeader className="bg-green-50">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-green-600" />
            Privacy Policy
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <textarea
            value={privacyContent}
            onChange={(e) => setPrivacyContent(e.target.value)}
            rows={12}
            className="w-full px-3 py-2 border rounded-md font-mono text-sm"
            placeholder="Enter Privacy Policy content..."
          />
          <div className="mt-4">
            <Button
              onClick={() => handleSave('privacy', privacyContent)}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Privacy Policy
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LegalManagement;